/**
 * AdminLayout — Sidebar admin refondue au style codevoiture.fr (Phase 30).
 *
 * Fond sombre #0a0a0b, sidebar #0d0d0e, brand rouge, items simples sans accordéon.
 * 9 items directs — aucun menu 'Plus' dépliable, tout est visible d'un coup.
 */
import React, { useEffect, useState } from 'react';
import { Outlet, Link, useNavigate, useLocation } from 'react-router-dom';
import {
  LayoutDashboard, BarChart3, Users, Inbox, Mail, FileText, BookOpenCheck, Sparkles, Building2,
  Shield, LogOut, ExternalLink, ArrowLeft, Wrench, Car, Presentation, MessageCircle, MapPinned,
  QrCode, FilePlus, Contact, Clapperboard,
} from 'lucide-react';
import { useApp } from '../contexts/AppContext';
import { RefreshButton } from './RefreshButton';
import api from '../api';

const NAV = [
  { to: '/admin/dashboard', label: 'Tableau de bord', icon: LayoutDashboard, exact: true, testid: 'admin-nav-dashboard' },
  { to: '/admin/shorts', label: 'Shorts', icon: Clapperboard, badgeKey: 'shorts', testid: 'admin-nav-shorts', highlight: true },
  { to: '/admin/ma-carte', label: 'Ma carte de visite', icon: Contact, testid: 'admin-nav-ma-carte' },
  { to: '/admin/analytics', label: 'Statistiques', icon: BarChart3, testid: 'admin-nav-analytics' },
  { to: '/admin/users', label: 'Utilisateurs', icon: Users, badgeKey: 'users', testid: 'admin-nav-users' },
  { to: '/admin/contact-requests', label: 'Demandes', icon: Inbox, badgeKey: 'contact', testid: 'admin-nav-contact-requests' },
  { to: '/admin/inbox', label: 'Inbox mail', icon: Mail, testid: 'admin-nav-inbox' },
  { to: '/admin/demandes-internes', label: 'Demandes internes', icon: MessageCircle, badgeKey: 'internal', testid: 'admin-nav-internal' },
  { to: '/admin/email-logs', label: 'Boîte mail', icon: Mail, testid: 'admin-nav-email-logs' },
  { to: '/admin/hero-editions', label: 'Magazine', icon: FileText, testid: 'admin-nav-hero-editions' },
  { to: '/admin/audit', label: 'Suivi livret', icon: BookOpenCheck, badgeKey: 'audit', testid: 'admin-nav-audit' },
  { to: '/admin/community', label: 'Actu & Fun', icon: Sparkles, badgeKey: 'community', testid: 'admin-nav-community' },
  { to: '/secretariat/planning', label: 'Secrétariat', icon: Building2, testid: 'admin-nav-secretariat' },
  { to: '/moniteur/planning', label: 'Enseignant', icon: Presentation, testid: 'admin-nav-moniteur' },
  { to: '/admin/voiture', label: 'Voiture', icon: Car, testid: 'admin-nav-voiture' },
  { to: '/admin/annuaire', label: 'Annuaire moto-écoles', icon: MapPinned, testid: 'admin-nav-annuaire' },
  { to: '/admin/b2b-plaquette', label: 'Plaquette B2B', icon: QrCode, testid: 'admin-nav-b2b-plaquette' },
  { to: '/admin/b2b-documents', label: 'Documents B2B', icon: FilePlus, testid: 'admin-nav-b2b-documents' },
  { to: '/admin/vcard-tracking', label: 'Carte de visite', icon: Contact, testid: 'admin-nav-vcard-tracking' },
  { to: '/admin/tools', label: 'Outils', icon: Wrench, testid: 'admin-nav-tools' },
];

export default function AdminLayout() {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useApp() || {};
  const [badges, setBadges] = useState({ users: 0, contact: 0, audit: 0, community: 0, internal: 0, shorts: 0 });

  useEffect(() => {
    (async () => {
      const b = { users: 0, contact: 0, audit: 0, community: 0, internal: 0, shorts: 0 };
      try { const s = await api.getAdminStats(); b.users = (s?.pending_applications || 0) + (s?.pending_reset_requests || 0); } catch (_) { /* silent */ }
      try { const cr = await api.adminListContactRequests('new'); b.contact = cr?.total || 0; } catch (_) { /* silent */ }
      try { const a = await api.getAdminAuditSummary(); if (a?.has_report) b.audit = a.counts.total_pending || 0; } catch (_) { /* silent */ }
      try { const c = await api.adminCommunitySummary(); b.community = (c.pending_questions || 0) + (c.pending_resources || 0); } catch (_) { /* silent */ }
      try { const i = await api.internalRequestCounts(); b.internal = (i?.new || 0) + (i?.in_progress || 0); } catch (_) { /* silent */ }
      try { const sc = await api.adminShortsCounts(); b.shorts = sc?.pending || 0; } catch (_) { /* silent */ }
      setBadges(b);
    })();
  }, [pathname]);

  const onLogout = () => { if (logout) logout(); navigate('/login'); };

  return (
    <div className="min-h-screen bg-[#0a0a0b] flex flex-col lg:flex-row" data-testid="admin-layout">
      {/* Mobile scroll hint CSS — dégradé fondu + animation subtile pour signaler
          que la nav horizontale défile. Désactivé en lg+ (sidebar verticale). */}
      <style>{`
        @media (max-width: 1023px) {
          .admin-nav-scroll {
            scroll-snap-type: x proximity;
            -webkit-overflow-scrolling: touch;
            scrollbar-width: none;
            mask-image: linear-gradient(to right, black 0, black calc(100% - 32px), transparent 100%);
            -webkit-mask-image: linear-gradient(to right, black 0, black calc(100% - 32px), transparent 100%);
          }
          .admin-nav-scroll::-webkit-scrollbar { display: none; }
          .admin-nav-scroll > a { scroll-snap-align: start; }
          .admin-nav-hint-floating {
            position: absolute;
            right: 8px;
            top: 50%;
            transform: translateY(-50%);
            z-index: 5;
            display: inline-flex;
            align-items: center;
            padding: 5px 10px;
            border-radius: 9999px;
            background: rgba(220, 38, 38, 0.9);
            color: #fff;
            font-size: 10px;
            font-weight: 800;
            letter-spacing: 0.05em;
            text-transform: uppercase;
            animation: adminNavHint 1.6s ease-in-out infinite;
            white-space: nowrap;
            pointer-events: none;
            box-shadow: 0 4px 12px rgba(220, 38, 38, 0.35);
          }
          @keyframes adminNavHint {
            0%, 100% { transform: translateY(-50%) translateX(0); opacity: 0.9; }
            50% { transform: translateY(-50%) translateX(-5px); opacity: 1; }
          }
        }
        @media (min-width: 1024px) {
          .admin-nav-hint-floating { display: none; }
        }
      `}</style>
      {/* ===== SIDEBAR ===== */}
      <aside className="lg:w-60 lg:min-h-screen shrink-0 border-b lg:border-b-0 lg:border-r border-white/10 bg-[#0d0d0e] flex lg:flex-col relative">
        <div className="hidden lg:flex items-center gap-2.5 px-5 h-16 border-b border-white/10">
          <div className="w-9 h-9 rounded-xl bg-red-600 text-white flex items-center justify-center shadow-lg shadow-red-900/40">
            <Shield size={16} />
          </div>
          <div>
            <div className="text-[9.5px] uppercase tracking-[0.22em] text-red-400 font-bold">code2roues.fr</div>
            <div className="text-white text-[14px] font-semibold leading-tight">Back-office</div>
          </div>
        </div>
        <nav className="flex lg:flex-col gap-1 p-2 lg:p-3 overflow-x-auto lg:overflow-visible flex-1 items-center lg:items-stretch admin-nav-scroll" data-testid="admin-sidebar-nav">
          {NAV.map((n) => {
            const Icon = n.icon;
            const active = n.exact ? pathname === n.to : pathname.startsWith(n.to);
            const count = n.badgeKey ? badges[n.badgeKey] : 0;
            const highlightClass = n.highlight && !active
              ? 'bg-gradient-to-r from-orange-500/15 to-transparent border border-orange-500/30 text-orange-200 hover:from-orange-500/25'
              : '';
            return (
              <Link
                key={n.to}
                to={n.to}
                data-testid={n.testid}
                className={`flex items-center gap-2.5 px-3.5 h-10 rounded-xl text-[13px] font-semibold whitespace-nowrap transition ${
                  active ? 'bg-red-600 text-white shadow-md shadow-red-900/40' : highlightClass || 'text-white/65 hover:bg-white/5 hover:text-white'
                }`}
              >
                <Icon size={15} className="shrink-0" />
                <span className="flex-1">{n.label}</span>
                {count > 0 && (
                  <span className={`min-w-[20px] h-5 px-1.5 rounded-full text-[10.5px] font-bold inline-flex items-center justify-center ${active ? 'bg-white text-red-600' : 'bg-red-600 text-white'}`}>
                    {count}
                  </span>
                )}
              </Link>
            );
          })}
        </nav>
        <span className="admin-nav-hint-floating" aria-hidden="true">Glissez →</span>
        <div className="hidden lg:block p-3 border-t border-white/10 space-y-1">
          <Link to="/" data-testid="admin-back-to-site" className="flex items-center gap-2.5 px-3.5 h-10 rounded-xl text-[13px] font-semibold text-white/65 hover:bg-white/5 hover:text-white transition">
            <ExternalLink size={14} /> Retour à l&apos;accueil
          </Link>
          <button onClick={onLogout} data-testid="admin-logout-btn" className="w-full flex items-center gap-2.5 px-3.5 h-10 rounded-xl text-[13px] font-semibold text-white/65 hover:bg-red-950/40 hover:text-red-300 transition">
            <LogOut size={14} /> Déconnexion
          </button>
          {user && <div className="px-3.5 pt-2 pb-1 text-white/35 text-[11px] truncate">{user.email}</div>}
        </div>
      </aside>

      {/* ===== CONTENU ===== */}
      <div className="flex-1 min-w-0">
        <header className="h-12 lg:h-16 border-b border-white/10 bg-[#0d0d0e]/60 backdrop-blur flex items-center justify-between px-4 lg:px-8">
          <div className="flex items-center gap-2 min-w-0">
            {pathname !== '/admin' && pathname !== '/admin/dashboard' && (
              <button
                onClick={() => (window.history.length > 1 ? navigate(-1) : navigate('/admin/dashboard'))}
                data-testid="admin-mobile-back"
                aria-label="Retour"
                className="lg:hidden shrink-0 -ml-1 w-9 h-9 rounded-lg border border-white/10 bg-white/5 text-white/80 active:scale-95 inline-flex items-center justify-center"
              >
                <ArrowLeft size={16} />
              </button>
            )}
            <div className="text-white/45 text-[12px] uppercase tracking-[0.2em] truncate">Administration</div>
          </div>
          <div className="flex items-center gap-2">
            {/* Accès rapide "Modération Shorts" — visible desktop + mobile, pulse si pending */}
            <Link
              to="/admin/shorts"
              data-testid="admin-header-shorts-shortcut"
              className={`relative hidden sm:inline-flex items-center gap-1.5 px-2.5 h-8 rounded-lg text-[11.5px] font-black uppercase tracking-wider transition ${
                badges.shorts > 0
                  ? 'bg-red-600 text-white shadow-lg shadow-red-900/40 hover:bg-red-500'
                  : 'bg-white/5 text-white/70 hover:bg-white/10 border border-white/10'
              }`}
              aria-label={`Modération Shorts${badges.shorts > 0 ? ` (${badges.shorts} en attente)` : ''}`}
            >
              <Clapperboard size={13} />
              <span>Shorts</span>
              {badges.shorts > 0 && (
                <>
                  <span className="min-w-[18px] h-[18px] px-1 rounded-full bg-white text-red-600 text-[10px] font-black inline-flex items-center justify-center ml-0.5">
                    {badges.shorts}
                  </span>
                  <span aria-hidden="true" className="absolute -top-1 -right-1 w-2.5 h-2.5">
                    <span className="absolute inset-0 rounded-full bg-red-400 animate-ping" />
                    <span className="relative block w-2.5 h-2.5 rounded-full bg-red-500" />
                  </span>
                </>
              )}
            </Link>
            <RefreshButton variant="admin" />
            <div className="flex items-center gap-2 lg:hidden">
              <Link to="/" data-testid="admin-back-to-site-mobile" className="text-white/60 text-[12px] inline-flex items-center gap-1"><ExternalLink size={12} /> Accueil</Link>
              <button onClick={onLogout} className="text-red-300 text-[12px] inline-flex items-center gap-1"><LogOut size={12} /> Quitter</button>
            </div>
          </div>
        </header>
        <main className="px-4 lg:px-8 py-6 lg:py-8" data-testid="admin-main">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
